import React from "react";
import { useFormik } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import { FetchDashboardLayout, SaveDashboardLayout } from "api/dashboardLayout";
import { DEFAULT_LAYOUT, countHidden, makeView, normalizeLayout } from "./catalog";

const QUERY_KEY = ["dashboard-layout"];

// The layout is a Formik form: `values` is the draft the dashboard previews instantly, `dirty` is
// edit mode, and saving is optimistic — the saved layout updates at once and the single PUT runs
// behind it. If the PUT fails the last saved layout comes back and the draft is kept, so nothing is lost.
const useDashboardLayout = () => {
  const queryClient = useQueryClient();
  const { data: saved, isLoading } = FetchDashboardLayout();
  const { mutateAsync: save } = SaveDashboardLayout();

  const initialValues = React.useMemo(() => (saved ? normalizeLayout(saved) : DEFAULT_LAYOUT), [saved]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    // No schema to check, so skip Formik's per-change validation pass.
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const { values, setFieldValue, setValues, resetForm, setStatus } = formik;
  const [isSaving, setIsSaving] = React.useState(false);

  // Applies the draft as the saved layout right away, then persists it. Resolves true on success.
  const saveOptimistic = React.useCallback(async (layout) => {
    const draft = layout || values;
    const previous = queryClient.getQueryData(QUERY_KEY);
    setStatus(null);
    queryClient.setQueryData(QUERY_KEY, draft);
    resetForm({ values: draft });
    setIsSaving(true);
    try {
      const res = await save(draft);
      const stored = res?.data?.data?.layout;
      if (stored) queryClient.setQueryData(QUERY_KEY, normalizeLayout(stored));
      return true;
    } catch (e) {
      queryClient.setQueryData(QUERY_KEY, previous);
      resetForm({ values: previous ? normalizeLayout(previous) : DEFAULT_LAYOUT });
      setValues(draft);
      setStatus("save_failed");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [values, queryClient, resetForm, setValues, setStatus, save]);

  const view = React.useMemo(() => makeView(values), [values]);
  const hiddenCount = React.useMemo(() => countHidden(values), [values]);

  return React.useMemo(() => ({
    values,
    view,
    hiddenCount,
    dirty: formik.dirty,
    isSaving,
    isLoading,
    saveFailed: formik.status === "save_failed",
    setWidget: (w, visible) => setFieldValue(`widgets.${w}.visible`, visible, false),
    setItem: (w, item, visible) => setFieldValue(`widgets.${w}.items.${item}`, visible, false),
    setOrder: (order) => setFieldValue("order", order, false),
    setItemOrder: (w, order) => setFieldValue(`itemOrder.${w}`, order, false),
    setDensity: (d) => setFieldValue("density", d, false),
    applyPreset: (preset) => setFieldValue("order", normalizeLayout({ order: preset.order }).order, false),
    resetDefault: () => setValues(DEFAULT_LAYOUT, false),
    discard: () => resetForm(),
    save: () => saveOptimistic(),
    // One click: the standard layout is saved at once; `undo` saves back what was on screen before.
    resetAndSave: async () => {
      const before = values;
      const ok = await saveOptimistic(DEFAULT_LAYOUT);
      return ok ? () => saveOptimistic(before) : null;
    },
  }), [values, view, hiddenCount, formik.dirty, formik.status, isSaving, isLoading, setFieldValue, setValues, resetForm, saveOptimistic]);
};

export default useDashboardLayout;
